import { type TypedDocumentNode, gql } from '@apollo/client';
import { add, isBefore, sub } from 'date-fns';
import type { PropsWithChildren } from 'react';

import {
  GameStatus,
  PlayerFieldStatus,
  Position,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { DisplayIfVisible } from '@sorare/core/src/atoms/layout/DisplayIfVisible';
import { SCORE_LIMIT } from '@sorare/core/src/components/player/FixtureChart';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import type { FilterKey, Filters, Options } from '#src/lib/performance.tsx';
import type { FixtureChart_playerGameScoreInterface } from '#src/shared/stats/LastScores/FixtureChart/__generated__/index.graphql.ts';
import type { Props as FixtureProps } from '#src/shared/stats/LastScores/FixtureChart/index.tsx';
import { FixtureChart } from '#src/shared/stats/LastScores/FixtureChart/index.tsx';

import type {
  LazyFixtureChartQuery,
  LazyFixtureChartQueryVariables,
  LazyFixtureChart_anyCard,
} from './__generated__/index.graphql.ts';

const emptyGameScore = (date: Date) =>
  ({
    __typename: 'PlayerGameScore' as const,
    id: date.toISOString(),
    score: 0,
    scoreStatus: ScoreStatus.DID_NOT_PLAY,
    positiveDecisiveStats: [],
    negativeDecisiveStats: [],
    anyGame: {
      __typename: 'Game' as const,
      id: date.toISOString(),
      date,
      status: GameStatus.scheduled,
      competition: {
        __typename: 'Competition' as const,
        slug: 'empty',
      },
      homeTeam: null,
      awayTeam: null,
    },
    projectedScore: 0,
    positionTyped: Position.Forward,
    projection: null,
    anyPlayer: {
      __typename: 'Player' as const,
      slug: 'empty',
    },
    anyPlayerGameStats: {
      __typename: 'PlayerGameStats' as const,
      anyTeam: {
        __typename: 'Club' as const,
        slug: 'empty',
        shortName: 'Empty',
        officialName: 'Empty',
        name: 'Empty',
        avatarUrl: null,
      },
      anyGame: {
        __typename: 'Game' as const,
        id: date.toISOString(),
        status: GameStatus.scheduled,
        competition: {
          __typename: 'Competition' as const,
          slug: 'empty',
          displayName: 'Empty',
        },
        so5Fixture: null,
        homeTeam: null,
        awayTeam: null,
      },
      id: date.toISOString(),
      gameStarted: 0,
      fieldStatus: PlayerFieldStatus.ON_BENCH,
      minsPlayed: 0,
      yellowCard: 0,
      footballPlayingStatusOdds: null,
    },
  }) satisfies FixtureChart_playerGameScoreInterface;

const EMPTY_GAME_SCORES = Array.from({ length: 20 }, (_, i) =>
  emptyGameScore(sub(Date.now(), { weeks: i }))
);

const LAZY_FIXTURE_CHART_QUERY = gql`
  query LazyFixtureChartQuery(
    $slug: String!
    $first: Int
    $after: String
    $position: Position
  ) {
    anyPlayer(slug: $slug) {
      slug
      anyFutureGames(first: 4) {
        nodes {
          id
          date
          playerGameScore(playerSlug: $slug, position: $position) {
            id
            ...FixtureChart_playerGameScoreInterface
          }
        }
      }
      allPlayerGameScores(first: $first, after: $after, position: $position) {
        nodes {
          id
          ...FixtureChart_playerGameScoreInterface
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
      ...FixtureChart_anyPlayer
    }
  }
  ${FixtureChart.fragments.anyPlayer}
  ${FixtureChart.fragments.playerGameScoreInterface}
` as TypedDocumentNode<LazyFixtureChartQuery, LazyFixtureChartQueryVariables>;

type Props = {
  playerSlug: string;
  card?: LazyFixtureChart_anyCard;
  disableScoreSelect?: boolean;
  onScoreSelect?: FixtureProps['onScoreSelect'];
  options?: Options;
  filters?: Filters;
  hideTitle?: boolean;
  onFilterChange?: (key: FilterKey, value: Filters[FilterKey]) => void;
  disableLinks?: boolean;
  showUpsell?: boolean;
  stickyFilters?: boolean;
};

const TWO_WEEKS_FROM_NOW = add(new Date(), { weeks: 2 });

const LazyFixtureChartComponent = ({
  playerSlug,
  card,
  onScoreSelect,
  disableScoreSelect,
  options,
  hideTitle,
  filters,
  onFilterChange,
  disableLinks,
  showUpsell = true,
  stickyFilters = false,
}: Props) => {
  const position = filters?.position || card?.anyPositions[0];
  const { data, loading, loadMore } = usePaginatedQuery(
    LAZY_FIXTURE_CHART_QUERY,
    {
      variables: { slug: playerSlug, first: SCORE_LIMIT, position },
      connection: 'PlayerGameScoreInterfaceConnection',
    }
  );

  const player = data?.anyPlayer;
  const { allPlayerGameScores, anyFutureGames } = player || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        slug: playerSlug!,
        after: data?.anyPlayer.allPlayerGameScores.pageInfo.endCursor,
        first: SCORE_LIMIT,
        position,
      });
    },
    allPlayerGameScores?.pageInfo.hasNextPage || false,
    loading
  );

  const previousSo5Scores = allPlayerGameScores?.nodes || EMPTY_GAME_SCORES;

  const nextSo5Scores =
    anyFutureGames?.nodes
      ?.map(
        anyGame =>
          anyGame &&
          isBefore(anyGame.date, TWO_WEEKS_FROM_NOW) &&
          anyGame.playerGameScore
      )
      .filter(Boolean) || [];
  const so5Scores = [...nextSo5Scores, ...previousSo5Scores];

  return (
    <FixtureChart
      player={player}
      card={card}
      hideTitle={hideTitle}
      so5Scores={so5Scores}
      onScoreSelect={onScoreSelect}
      InfiniteScrollLoader={<InfiniteScrollLoader />}
      disableScoreSelect={disableScoreSelect}
      options={options}
      filters={filters}
      onFilterChange={onFilterChange}
      disableLinks={disableLinks}
      showUpsell={showUpsell}
      stickyFilters={stickyFilters}
      loading={loading}
    />
  );
};

export const LazyFixtureChart = ({
  children,
  ...props
}: PropsWithChildren<Props>) => {
  return (
    <DisplayIfVisible>
      <LazyFixtureChartComponent {...props} />
    </DisplayIfVisible>
  );
};

LazyFixtureChart.fragments = {
  card: gql`
    fragment LazyFixtureChart_anyCard on AnyCardInterface {
      slug
      anyPositions
      ...FixtureChart_anyCard
    }
    ${FixtureChart.fragments.anyCard}
  ` as TypedDocumentNode<LazyFixtureChart_anyCard>,
};
